import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-loader';
import '@vaadin/grid';
import '@vaadin/grid/vaadin-grid-column.js';
import { css, html, nothing, type TemplateResult } from 'lit';
import { gridRowDetailsRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import '../balance/balance-report-columns';
// eslint-disable-next-line no-duplicate-imports
import {
    balanceReportColumns,
    renderAccountBalanceRecordDetails,
} from '../balance/balance-report-columns';
import { BalanceReportItemVm } from '../balance/balance-report-item.vm';
import { ReportMetaDataVm } from '../data/report-meta-data.vm';
import '../edi/edi-report-columns';
// eslint-disable-next-line no-duplicate-imports
import { ediReportColumns } from '../edi/edi-report-columns';
import { EdiReportItemVm } from '../edi/edi-report-item.vm';
import '../transaction/transaction-report-columns';
// eslint-disable-next-line no-duplicate-imports
import {
    renderTransactionRecordDetails,
    transactionReportColumns,
} from '../transaction/transaction-report-columns';
import { TransactionReportItemVm } from '../transaction/transaction-report-item.vm';
import '../transaction/transactions-account-detail-sheet';

export const tagName = 'report-table';
@customElement(tagName)
export class ReportTable extends TmBaseComponent {
    @property({ type: Boolean })
    public tableLoading = true;

    @property()
    public report?: ReportMetaDataVm;

    @property({ type: Array })
    public records: [] | TransactionReportItemVm[] | EdiReportItemVm[] = [];

    @state()
    private openedRecords: TransactionReportItemVm[] = [];

    @state()
    private transactionAccount?: TransactionReportItemVm;

    @state()
    private columns: TemplateResult<1> | typeof nothing = nothing;

    public updated(changedProps: Map<string | number | symbol, unknown>) {
        if (changedProps.has('records') && this.records && this.report) {
            this.getColumns();
        }
    }

    public render() {
        if (this.tableLoading) {
            return html`<div class="flex items-center justify-center h-80">
                <tm-loader card></tm-loader>
            </div>`;
        }
        return html`<vaadin-grid
                id="report-table"
                .items=${this.records}
                .detailsOpenedItems=${this.openedRecords}
                @balance-account-clicked=${(e: CustomEvent) => {
                    const record = e.detail;
                    let isOpened = false;
                    isOpened = this.openedRecords.includes(record);
                    this.openedRecords = isOpened
                        ? this.openedRecords.filter(r => r !== record)
                        : [...this.openedRecords, record];
                }}
                @transaction-account-clicked=${(e: CustomEvent) => {
                    this.transactionAccount = e.detail;
                }}
                ${gridRowDetailsRenderer<TransactionReportItemVm | BalanceReportItemVm>(record => {
                    return this.getRecordDetails(record);
                }, [])}
            >
                ${this.columns}
                <div class="w-full text-center mt-10" slot="empty-state">
                    No records found.
                </div> </vaadin-grid
            ><transactions-account-detail-sheet
                .open=${!!this.transactionAccount}
                .account=${this.transactionAccount}
                .report=${this.report}
                @close=${() => {
                    this.transactionAccount = undefined;
                }}
            ></transactions-account-detail-sheet>`;
    }

    private getColumns() {
        if (!this.report) {
            return;
        }
        if (this.report.reportType.toLowerCase() === 'balance') {
            this.columns = balanceReportColumns(this.records as BalanceReportItemVm[]);
        } else if (this.report.reportType.toLowerCase() === 'edi') {
            this.columns = ediReportColumns();
        } else {
            this.columns = transactionReportColumns(this.records as TransactionReportItemVm[]);
        }
    }

    private getRecordDetails(record: TransactionReportItemVm | BalanceReportItemVm) {
        if (record.isTransaction) {
            return renderTransactionRecordDetails(record as TransactionReportItemVm);
        } else {
            return renderAccountBalanceRecordDetails(record as BalanceReportItemVm);
        }
    }

    public static get styles() {
        return css`
            :host {
                display: block;
            }
            #report-table {
                height: calc(100vh - 177px);
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ReportTable;
    }
}
